import {
  useDiningsStore, useEventsStore, useFacilitiesStore, useHotelStore,
  useLabelsStore, useMeetingsStore, usePagesStore, useRoomsStore,
  useSettingStore,
} from 'pinia-store'
import { useRoute, useRouter } from 'vue-router'
import { Drawer } from 'flowbite'
import { breakpointsTailwind, useBreakpoints, useWindowScroll } from '@vueuse/core'
import type { DrawerOptions, InstanceOptions } from 'flowbite'
import { stripHtml } from '@general-function'
import { initMenu } from '@ui/composables'
import type { StringArraySupportOption } from 'prettier'
import { useI18n, useLocalePath } from '#imports'

// Exports
export function useDataHeader() {
  const breakpoints = useBreakpoints(breakpointsTailwind)
  const isMobile = breakpoints.smallerOrEqual('lg')

  const { y } = useWindowScroll()

  const { locale: language, t } = useI18n()
  const localePath = useLocalePath()
  const useSetting = useSettingStore()
  const useHotel = useHotelStore()
  const useLabels = useLabelsStore()
  const useFacilities = useFacilitiesStore()
  const useMeetings = useMeetingsStore()
  const useRooms = useRoomsStore()
  const useDinings = useDiningsStore()
  const useEvents = useEventsStore()

  const labels = useLabels.getLabels
  const profile = useHotel.getProfile
  const usePages = usePagesStore()
  const isOnTop = computed(() => y.value === 0)
  const drawer = ref({})
  const route = useRoute()
  const router = useRouter()

  const isHome = computed(() => route?.name && route.name === 'home')
  const isMounted = ref(false)
  const meetings = computed(() => useMeetings.getList)
  const events = computed(() => useEvents.getList)
  const rooms = computed(() => useRooms.getList)
  const loading = ref(false)
  const facilities = computed(() => useFacilities.getList)
  const offers = ref([1])
  const press = ref([1])
  const dinings = computed(() => useDinings.getList)
  const headerMenu = document && document.getElementById('header-menu')
    ? document.getElementById('header-menu')
    : undefined
  const headerLogo = document && document.getElementById('header-logo')
    ? document.getElementById('header-logo')
    : undefined
  const menuPosition = ref(headerMenu?.getBoundingClientRect().bottom || 0)
  const logoPosition = ref(headerLogo?.getBoundingClientRect().bottom
  && headerLogo?.getBoundingClientRect().bottom >= 50
    ? headerLogo?.getBoundingClientRect().bottom
    : 50)
  const mobileView = ref(false)
  const showButton = false
  const menu = computed(() => initMenu(usePages.getMenu))
  const isAtNotFoundPage = computed(() => route.name === 'not-found')

  interface VisibleProps {
    about: boolean
    rooms: boolean
    meetings: boolean
    facilities: boolean
    photos: boolean
    news: boolean
    contact: boolean
    book: boolean
  }

  const visible = ref<VisibleProps>({
    about: false,
    rooms: false,
    meetings: false,
    facilities: false,
    photos: false,
    news: false,
    contact: false,
    book: true,
  })

  function createMenuLink(obj) {
    let req = obj
    if (isAtNotFoundPage) {
      req = {
        ...req,
        params: {
          ...req?.params,
          language: route.params?.language || language.value,
        },
      }
    }
    else {
      req = localePath({ ...req })
    }
    return req
  }
  const btnLabel = stripHtml(labels?.ROOMS_PAGE_BTN_LABEL, false, 'Book Now')

  function init() {
    const targetEl = document.getElementById('drawer-body-scrolling') as HTMLElement | null
    if (!targetEl)
      return

    // options with default values
    const options: DrawerOptions = {
      placement: 'right',
      backdrop: true,
      bodyScrolling: false,
      edge: false,
      edgeOffset: '',
      backdropClasses:
          'bg-gray-900/80 !dark:bg-gray-900/80 fixed inset-0 z-40',
      onHide: () => {
        handleClickMenu(false)
      },
      onShow: () => {
        handleClickMenu(true)
      },
      onToggle: () => {
      },
    }

    // instance options object
    const instanceOptions: InstanceOptions = {
      id: 'drawer-body-scrolling',
      override: true,
    }

    /*
   * targetEl (required)
   * options (optional)
   * instanceOptions (optional)
   */
    drawer.value = new Drawer(targetEl, options, instanceOptions)
  }
  watch(route, (to) => {
    if (to.name)
      useSetting.setShowSidebarMenu(false)
    if (to?.query?.page && to.name === 'home' && typeof document !== 'undefined') {
      setTimeout(() => {
        const el = document.getElementById(`section-${to.query.page}`)
        if (el)
          el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }, { flush: 'pre', immediate: true, deep: true })
  function handleScroll() {
    if (window && typeof window !== 'undefined')
      detectId()
  }
  function checkMenu(slug) {
    let bool = true
    if (slug === 'whats-new' && (!press.value || press.value.length === 0))
      bool = false
    else if (slug === 'offers' && (!offers.value || offers.value.length === 0))
      bool = false
    else if (slug === 'facilities' && (!facilities.value || facilities.value.length === 0))
      bool = false
    else if (slug === 'meetings' && (!meetings.value || meetings.value.length === 0))
      bool = false
    else if (slug === 'events' && (!events.value || events.value.length === 0))
      bool = false
    else if (slug === 'rooms' && (!rooms.value || rooms.value.length === 0))
      bool = false
    else if (slug === 'dinings' && (!dinings.value || dinings.value.length === 0))
      bool = false
    return bool
  }

  function handleClickBurger() {
    if (!useSetting.showSidebarMenu && drawer.value && Object.keys(drawer.value).length)
      drawer.value.show()

    else if (drawer.value && Object.keys(drawer.value).length)
      drawer.value.hide()
  }
  function handleHideSidebar() {
    drawer.value.hide()
  }
  function handleClickMenu(isBool?: boolean) {
    useSetting.setShowSidebarMenu(isBool !== undefined && typeof isBool
    === 'boolean'
      ? isBool
      : !useSetting.showSidebarMenu)
  }
  const enableScrollViews = ref(true)
  const viewedSection = computed(() => {
    const el = {}
    let arr: any = []
    if (menu.value && menu.value.length) {
      arr = menu.value.map(({ slug }: any) => {
        return slug
      })
    }
    if (arr && arr.length)
      Object.assign(el, arr)
    !(press.value && press.value.length && el?.['whats-new'] !== undefined) && delete el['whats-new']
    // !(meeting.length) && delete el.meetings
    !(facilities.value && facilities.value.length && el?.facilities !== undefined) && delete el.facilities
    !(offers.value && offers.value.length && el?.offers !== undefined) && delete el.offers
    return el
  })
  function handleClick(val: {
    slug: string
    child?: StringArraySupportOption
    is_spa?: boolean
  }) {
    enableScrollViews.value = false
    handleHideSidebar()
    if (!val?.slug)
      return

    const el = document.getElementById(`section-${val.slug}`)
    const name = route.name
    if (!name)
      return
    if (val?.is_spa
    && name !== 'home') {
      return router.push(createMenuLink({
        name: 'home',
        params: {
          language: route.params?.language || language.value,
          slug: val?.child,
        },
        query: {
          page: val.slug,
        },
      }))
    }
    else if (el && val?.is_spa) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
    else if (val?.slug && !val?.is_spa) {
      return router.push(createMenuLink({
        name: val?.slug,
        params: {
          slug: val?.child,
        },
      }))
    }
  }
  function detectId() {
    const name = route.name
    if (Object.keys(viewedSection.value).length === 0 || name !== 'home')
      return

    const headerNav = document.querySelector('.header-nav')
    const appBar = document.getElementById('app-bar')
    if (!headerNav || !appBar)
      return
    const offset = appBar.offsetHeight
    Object.keys(viewedSection.value).forEach((i) => {
      let el: any = document.getElementById(`section-${viewedSection.value[i]}`)
      if (!el)
        return (visible.value[i] = false)
      const { offsetHeight } = el
      el = el.getBoundingClientRect()
      visible.value[i] = el.top - offset > -offsetHeight * 0.5
        && el.bottom
        < (window.innerHeight || document.documentElement.clientHeight)
        + offsetHeight * 0.5
      //
      if (
        enableScrollViews.value
        && !viewedSection[i]
        && visible.value[i]
      )
        viewedSection[i] = true
      // gtagViewEvent(this.viewedSection[i].text)
    })
  }

  onMounted(async () => {
    isMounted.value = true
    await init()
    await detectId()
    if (typeof window !== 'undefined')
      window.addEventListener('scroll', handleScroll)

    await nextTick(() => {
      if (document) {
        mobileView.value = false
        if (headerLogo)
          logoPosition.value = headerLogo.getBoundingClientRect().bottom
        if (headerMenu && logoPosition.value && headerMenu.getBoundingClientRect().bottom >= logoPosition.value) {
          menuPosition.value = headerMenu.getBoundingClientRect().bottom
          mobileView.value = true
        }
      }
    })
  })

  onBeforeUnmount(() => {
    isMounted.value = false
    if (typeof window !== 'undefined')
      window.removeEventListener('scroll', handleScroll)
  })

  watch(
    isMobile,
    (cur) => {
      if (document && document !== undefined) {
        if (headerMenu && headerMenu.getBoundingClientRect().bottom >= logoPosition.value && !mobileView.value)
          mobileView.value = true
        else if (headerMenu && headerMenu.getBoundingClientRect().bottom < logoPosition.value)
          mobileView.value = false
      }
      if (useSetting.showSidebarMenu && cur)
        handleClickBurger()
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    useSetting.setShowSidebarMenu(false)
  })

  return {
    isMounted,
    loading,
    isOnTop,
    profile,
    createMenuLink,
    handleHideSidebar,
    mobileView,
    isHome,
    handleClickBurger,
    btnLabel,
    menu,
    handleClick,
    checkMenu,
    localePath,
    visible,
    route,
    labels,
    showButton,
    t,
    logoPosition,
  }
}
